import { Controller } from "@hotwired/stimulus"

// Live frame sync for the viewer's own avatars (#419).
//
// Equipping/unequipping a frame must update EVERY avatar of the signed-in
// user already on the page (navbar, feed composer, own posts/comments,
// comment form, chat input) — not just the surface where the equip
// happened. Before this, only the equip surface re-rendered; the rest
// kept the old frame until a full reload (F5).
//
// Mechanism (server-authoritative, no optimistic client state):
//   * Each of the viewer's own framed avatars carries `data-self-avatar-frame`
//     on its `.avatar-frame-wrapper` (UserAvatarComponent#self_sync_target?).
//   * The equip response turbo-streams the freshly-rendered frame overlay
//     into this relay's <template> (`data-self-frame-relay-target="frame"`).
//     The overlay is the SAME CosmeticRendererComponent every surface uses
//     (poster fix included) and is fluid (100% of its concentric wrapper),
//     so one fragment fits every avatar size.
//   * On arrival we swap the overlay inside every marked wrapper. An empty
//     payload means the frame was unequipped → leave the bare avatar.
export default class extends Controller {
  static targets = ["frame"]

  // Fires when the equip response inserts a fresh <template> payload.
  frameTargetConnected(tpl) {
    const tier = tpl.dataset.tier || ""
    // `<template>.innerHTML` serializes its content; empty = unequipped.
    const html = (tpl.innerHTML || "").trim()

    document.querySelectorAll("[data-self-avatar-frame]").forEach((wrap) => {
      this.#applyTo(wrap, html, tier)
    })
  }

  #applyTo(wrap, html, tier) {
    // Drop the current overlay. Its dotlottie-player controller tears down
    // its WASM player + canvas via disconnect() (no leak).
    const existing = wrap.querySelector(":scope > .cosmetic-renderer--avatar-frame")
    if (existing) existing.remove()

    // Keep the rarity hook (#402 glow) in sync.
    if (tier) wrap.setAttribute("data-tier", tier)
    else wrap.removeAttribute("data-tier")

    // Inject the new overlay as a DIRECT child so the concentric rule
    // `.avatar-frame-wrapper > .cosmetic-renderer--avatar-frame` applies.
    // Stimulus auto-connects the injected dotlottie-player. Empty html
    // (unequip) leaves just the bare avatar.
    if (html) wrap.insertAdjacentHTML("beforeend", html)
  }
}
